import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { createOrder } from "@/lib/api";
import CheckoutForm from "./components/CheckoutForm";
import type { FormData } from "./components/CheckoutForm";
import OrderSummary from "./components/OrderSummary";
import OrderSuccess from "./components/OrderSuccess";

export default function CheckoutPage() {
  const { items, total, clearCart } = useCart();
  const [shippingCost, setShippingCost] = useState(0);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (items.length === 0 && !orderPlaced) {
    return <Navigate to="/" replace />;
  }

  if (orderPlaced) {
    return <OrderSuccess email={confirmedEmail} orderNumber={orderNumber} />;
  }

  const handleOrderSubmit = async (formData: FormData) => {
    setSubmitting(true);
    try {
      const subtotal = total;
      const tax = (subtotal - promoDiscount) * 0.085;
      const orderTotal = subtotal - promoDiscount + shippingCost + tax;

      const result = await createOrder({
        email: formData.email,
        phone: formData.phone,
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: formData.company,
        address: formData.address,
        apt: formData.apt,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country || "United States",
        subtotal,
        shippingCost,
        promoDiscount,
        tax,
        total: orderTotal,
        shippingMethod: "standard",
        items: items.map((item) => ({
          productId: item.productId,
          name: item.name,
          price: item.price,
          size: item.size,
          color: item.color,
          colorHex: item.colorHex,
          quantity: item.quantity,
          image: item.image,
        })),
      });

      setConfirmedEmail(formData.email);
      setOrderNumber(result.orderNumber);
      clearCart();
      setOrderPlaced(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Order failed:", err);
      alert("Order submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" style={{ fontFamily: "'Jost', sans-serif" }}>
      {/* Minimal Header */}
      <header className="bg-white border-b border-pearl/50 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://static.readdy.ai/image/11f299ce572862a7443fa4f5e7dec340/39eaa7af49292fe352c32005f65aa55b.png"
              alt="i-if"
              className="h-8 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-2 text-xs text-midnight/40">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-lock-fill text-sm text-midnight/30" />
            </div>
            <span className="tracking-wide">Secure Checkout</span>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-6 py-3">
        <div className="flex items-center gap-2 text-[10px] text-midnight/35 tracking-wide">
          <Link to="/" className="hover:text-midnight transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <Link to="/" className="hover:text-midnight transition-colors cursor-pointer">Cart</Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-midnight/60">Checkout</span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 items-start">
          {/* Left: Form */}
          <div className="bg-white rounded-lg p-8 border border-pearl/40">
            {submitting && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                <div className="text-sm text-midnight/50">Processing order...</div>
              </div>
            )}
            <CheckoutForm
              shippingCost={shippingCost}
              onShippingChange={setShippingCost}
              onSubmit={handleOrderSubmit}
            />
          </div>

          {/* Right: Summary */}
          <div>
            <OrderSummary
              shipping={shippingCost}
              promoDiscount={promoDiscount}
              onPromoApply={setPromoDiscount}
            />
          </div>
        </div>
      </div>

      {/* Minimal Footer */}
      <div className="border-t border-pearl/40 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-[10px] text-midnight/30">
          <span>&copy; 2026 i-if. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <a href="/pages/privacy" className="hover:text-midnight transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</a>
            <a href="/pages/returns" className="hover:text-midnight transition-colors cursor-pointer whitespace-nowrap">Returns</a>
            <a href="/pages/shipping" className="hover:text-midnight transition-colors cursor-pointer whitespace-nowrap">Shipping</a>
          </div>
        </div>
      </div>
    </div>
  );
}
